import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, PauseIcon, PlayIcon, SkipForwardIcon } from 'lucide-react';
import { HeaderStage } from '../components/HeaderStage';
import { Waveform } from '../components/Waveform';
import { Visualizer, type VisualizerStyle } from '../components/Visualizer';
import { usePlayer } from '../contexts/PlayerContext';
import { useLibrary } from '../contexts/LibraryContext';
import { useLyrics } from '../hooks/useLyrics';
import { formatTime } from '../utils/format';
interface NowPlayingProps {
  visualizerStyle: VisualizerStyle;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NowPlaying({
  visualizerStyle
}: NowPlayingProps) {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    toggle,
    next
  } = usePlayer();
  const {
    isFavorite,
    toggleFavorite
  } = useLibrary();
  const {
    status,
    lines
  } = useLyrics(currentTrack);
  if (!currentTrack) {
    return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-331" data-mp="2uut5bqrzr77k">
        <HeaderStage data-id="element-332" data-mp="33f0n6g3q3k5l" />
        <div className="relative z-10 max-w-md" data-id="element-333" data-mp="33eimn7a12jrv">
          <h1 className="font-display text-4xl text-cream" data-id="element-334" data-mp="38fodezk4nwyr">Nothing on the turntable</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-335" data-mp="2ufdow36aiugr">
            Start a track and this room fills in — cover art, a live wave off the actual audio, and the words where
            we can find them.
          </p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-336" data-mp="3n3sz8ceh8olr">
            Back to tonight&apos;s set
          </Link>
        </div>
      </div>;
  }
  return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-337" data-mp="9vg7v8cuew3l">
      <HeaderStage artwork={currentTrack.artwork} data-id="element-338" data-mp="ep14ox2p9k17" />

      <div className="relative z-10 grid gap-10 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-14" data-id="element-339" data-mp="ok84sfaku4oq">
        {/* The record */}
        <motion.section aria-label="Now playing" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-340" data-mp="jm6jdnpohta3">
          <div className="relative overflow-hidden rounded-xl ring-1 ring-cream/10" data-id="element-341" data-mp="9m5wjrslov15">
            <img src={currentTrack.artwork} alt={`${currentTrack.album} cover art`} className="aspect-square w-full object-cover" data-id="element-342" data-mp="5lly8kwajldf" />
            <div className="absolute -bottom-6 -right-6 h-28 w-28" data-id="element-343" data-mp="3s0f7aih99vvx">
              <Visualizer style={visualizerStyle} className="h-full w-full" data-id="element-344" data-mp="3rylvkorsclh2" />
            </div>
          </div>

          <p className="mt-6 text-xs text-muted" data-id="element-345" data-mp="3i2r7vf8pg07f">{currentTrack.genre || 'Uncategorised'}</p>
          <h1 className="mt-1 font-display text-4xl leading-tight text-cream" data-id="element-346" data-mp="3i4lnqf54d24n">{currentTrack.title}</h1>
          <p className="mt-2 text-sm text-muted" data-id="element-347" data-mp="1tj4zvjc5tsyl">
            {currentTrack.artist} · {currentTrack.album}
          </p>

          {/* Live wave off the real audio */}
          <div className="mt-6 h-16 rounded-lg border border-ink-line bg-ink-panel/50 px-3 py-2" data-id="element-348" data-mp="1xiwzh6jksort">
            <Waveform data-id="element-349" data-mp="22kmaim6st2fw" />
          </div>
          <p className="mt-2 text-[11px] tabular-nums text-muted" data-id="element-350" data-mp="27inv5bjvh9th">
            {formatTime(progress)} / {formatTime(duration)} preview
          </p>

          <div className="mt-6 flex items-center gap-3" data-id="element-351" data-mp="19wqj9x0002qi">
            <button type="button" onClick={toggle} aria-label={isPlaying ? 'Pause' : 'Play'} className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-ink-base transition-transform duration-150 ease-night hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-352" data-mp="1etp943cmpniw">
              {isPlaying ? <PauseIcon className="h-5 w-5" aria-hidden="true" data-id="element-353" data-mp="2xswn9o9a5m3d" /> : <PlayIcon className="h-5 w-5 translate-x-[1px]" aria-hidden="true" data-id="element-354" data-mp="2xqk7nhqzvnth" />}
            </button>
            <button type="button" onClick={next} aria-label="Next track" className="rounded-full border border-ink-line p-3 text-muted transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-355" data-mp="37ig6ww4thjza">
              <SkipForwardIcon className="h-4 w-4" aria-hidden="true" data-id="element-356" data-mp="3chb26xjz110a" />
            </button>
            <button type="button" onClick={() => toggleFavorite(currentTrack)} aria-pressed={isFavorite(currentTrack.id)} aria-label={isFavorite(currentTrack.id) ? 'Remove from saved' : 'Save track'} className="rounded-full border border-ink-line p-3 text-muted transition-colors duration-150 ease-night hover:border-iris-soft/60 hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-357" data-mp="3qh27m9wf992o">
              <HeartIcon className={`h-4 w-4 ${isFavorite(currentTrack.id) ? 'fill-iris-soft text-iris-soft' : ''}`} aria-hidden="true" data-id="element-358" data-mp="bl0gdm88x77n" />
            </button>
          </div>
        </motion.section>

        {/* Lyrics */}
        <motion.section aria-labelledby="lyrics-heading" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: EASE
      }} className="min-w-0" data-id="element-359" data-mp="3k12bpyi9fy2j">
          <div className="flex items-baseline justify-between gap-4 border-b border-ink-line pb-4" data-id="element-360" data-mp="3b307la38cpb1">
            <h2 id="lyrics-heading" className="font-display text-3xl text-cream" data-id="element-361" data-mp="3f4259mhi0taw">
              Lyrics
            </h2>
            <p className="text-xs text-muted" data-id="element-362" data-mp="1dam3pujvhu15">
              {status === 'found' ? 'from the open lyrics archive' : 'no time-sync — read at your own pace'}
            </p>
          </div>

          <div aria-live="polite" className="mt-6" data-id="element-363" data-mp="2h8ptpkwfzwuk">
            {status === 'loading' && <ul className="space-y-3" data-id="element-364" data-mp="2d74kc9j4a0p4">
                {Array.from({
              length: 10
            }).map((_, i) => <li key={i} className="h-3.5 animate-pulse rounded bg-ink-raised" style={{
              width: `${45 + i * 37 % 45}%`
            }} data-id="element-365" data-mp="2881ggsevi0iq" data-mpi={i} />)}
              </ul>}

            {status === 'found' && <div className="max-h-[52vh] overflow-y-auto scroll-thin pr-4" data-id="element-366" data-mp="2w60ddhj8o91d">
                {lines.map((line, i) => line.length === 0 ? <span key={`gap-${i}`} className="block h-4" data-id="element-367" data-mp="2r6ng02uqrjkj" /> : <motion.p key={`${i}-${line.slice(0, 12)}`} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease: EASE,
              delay: Math.min(i, 14) * 0.02
            }} className="font-display text-lg leading-relaxed text-cream/90" data-id="element-368" data-mp="3onukpc0uzyat">
                      {line}
                    </motion.p>)}
              </div>}

            {status === 'missing' && <div className="max-w-md" data-id="element-369" data-mp="e0pe70rjdcyu">
                <p className="font-display text-2xl italic text-gold" data-id="element-370" data-mp="dzxk5udl7nao">No words for this one</p>
                <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-371" data-mp="ixpo5mriazv8">
                  Instrumentals, library cuts, and obscure pressings usually come back empty. The wave beside it is
                  the whole story tonight.
                </p>
              </div>}

            {status === 'error' && <p className="rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-372" data-mp="wouf80gt6jty">
                The lyrics archive didn&apos;t answer. Skip to the next record or try again in a moment.
              </p>}
          </div>
        </motion.section>
      </div>
    </div>;
}